import { useEffect, useState } from "react";
import { message, type FormInstance } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchDashboards, fetchUserDashboards } from "src/services/UserManagementAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import type { DashboardSelectOption, User, UserFormMode, UserFormValues } from "../Types/UserTypes";
import { ALL_DASHBOARDS_VALUE } from "../Utils/UserConstants";

interface UseDashboardOptionsParams {
  form: FormInstance<UserFormValues>;
  isOpen: boolean;
  mode: UserFormMode;
  user: User | null;
  apiKey: string;
  tenantId: string;
}

const resolveAllDashboardsSelection = (
  previous: string[],
  next: string[],
  options: DashboardSelectOption[]
): string[] => {
  const dashboardValues = options
    .filter(option => option.value !== ALL_DASHBOARDS_VALUE)
    .map(option => option.value);
  const hadAllSelected = previous.includes(ALL_DASHBOARDS_VALUE);
  const hasAllSelected = next.includes(ALL_DASHBOARDS_VALUE);

  if (hasAllSelected && !hadAllSelected) {
    return [ALL_DASHBOARDS_VALUE, ...dashboardValues];
  }
  if (hadAllSelected && !hasAllSelected) {
    return [];
  }

  const individualValues = next.filter(value => value !== ALL_DASHBOARDS_VALUE);
  const isEveryDashboardSelected =
    dashboardValues.length > 0 && individualValues.length === dashboardValues.length;

  return isEveryDashboardSelected ? [ALL_DASHBOARDS_VALUE, ...dashboardValues] : individualValues;
};

export const useDashboardOptions = ({
  form,
  isOpen,
  mode,
  user,
  apiKey,
  tenantId,
}: UseDashboardOptionsParams) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = getApiLangFromI18n(i18n);
  const [dashboardOptions, setDashboardOptions] = useState<DashboardSelectOption[]>([]);
  const [selectedDashboards, setSelectedDashboards] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!isOpen || (mode === "edit" && !user)) {
      return;
    }

    const loadAddOptions = async () => {
      const dashboards = await fetchDashboards(tenantId, apiKey, lang);
      setDashboardOptions([
        { label: t("userManagement.modals.addUser.allDashboards"), value: ALL_DASHBOARDS_VALUE },
        ...dashboards.map(({ name, value }) => ({ label: name, value })),
      ]);
    };

    const loadEditOptions = async (editedUser: User) => {
      const dashboards = await fetchUserDashboards(apiKey, tenantId, editedUser.id, lang);
      const assigned = dashboards
        .filter(dashboard => dashboard.tick_mark)
        .map(dashboard => dashboard.dashboard_id.toString());
      setDashboardOptions(
        dashboards.map(dashboard => ({
          label: dashboard.dashboard_name,
          value: dashboard.dashboard_id.toString(),
        }))
      );
      setSelectedDashboards(assigned);
      form.setFieldsValue({ dashboardAccess: assigned });
    };

    const loadOptions = async () => {
      setIsFetching(true);
      try {
        await (user ? loadEditOptions(user) : loadAddOptions());
      } catch (error) {
        if (error instanceof Error && error.message === "unauthorized") {
          navigate("/log-in");
        } else {
          message.error(
            t(
              mode === "edit"
                ? "userManagement.modals.editUser.fetchDashboardsFailed"
                : "userManagement.modals.addUser.fetchDashboardsFailed"
            )
          );
        }
      } finally {
        setIsFetching(false);
      }
    };

    loadOptions();
  }, [apiKey, form, isOpen, lang, mode, navigate, t, tenantId, user]);

  const changeSelection = (value: string[]) => {
    const next =
      mode === "add" ? resolveAllDashboardsSelection(selectedDashboards, value, dashboardOptions) : value;
    setSelectedDashboards(next);
    form.setFieldsValue({ dashboardAccess: next });
  };

  const resetSelection = () => setSelectedDashboards([]);

  return { dashboardOptions, isFetching, changeSelection, resetSelection };
};
